/**
 * Keeps the content element and IMA's ads manager on the same volume.
 *
 * IMA renders linear breaks in #ad-container and only honours
 * `adsManager.setVolume()` — setting `video.muted` alone is inaudible for
 * the whole break, which is how a scenario Unmute produced a silent evidence
 * video. Mirrors apps/web/src/lib/preview-ima.ts.
 *
 * CESP-964: the scenario carries a level in the 0..1 scale, not a switch, so
 * the writer takes a level and mute is the level 0. A gradual transition is
 * a sequence of these calls; whoever owns the ramp drives it (the runner
 * steps it inside one page call, see apps/runner/src/engines/webVolume.ts).
 *
 * @param {HTMLVideoElement} video
 * @param {() => { setVolume?: (volume: number) => void } | null} getAdsManager
 */
export function createApplyVolume(video, getAdsManager) {
  return function applyVolume(level) {
    const wanted = Math.min(1, Math.max(0, Number.isFinite(level) ? level : 1));
    video.volume = wanted;
    // A level of 0 is a mute, and the element carries both: leaving `muted`
    // set while the volume climbs again would keep the content silent.
    video.muted = wanted <= 0;
    try {
      getAdsManager()?.setVolume?.(wanted);
    } catch {
      /* IMA rejects setVolume outside an initialised ad break */
    }
  };
}

/**
 * The mute switch the publisher control and the layout baseline still speak.
 *
 * @param {HTMLVideoElement} video
 * @param {() => { setVolume?: (volume: number) => void } | null} getAdsManager
 */
export function createApplyMuted(video, getAdsManager) {
  const applyVolume = createApplyVolume(video, getAdsManager);
  return function applyMuted(muted) {
    applyVolume(muted ? 0 : 1);
  };
}
